import { useEffect, useState, useCallback } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { useAppStore } from '@/store/useAppStore'
import { Button } from '@/components/ui/Button'
import { supabase } from '@/lib/supabase'
import { motion } from 'framer-motion'

export default function InvitePage() {
  const { token } = useParams()
  const navigate = useNavigate()
  const { isAuthenticated, acceptInvite, currentUser } = useAppStore()
  const [invite, setInvite] = useState(null)
  const [loading, setLoading] = useState(true)
  const [accepting, setAccepting] = useState(false)
  const [error, setError] = useState(null)
  const [done, setDone] = useState(false)

  const handleAccept = useCallback(async () => {
    setAccepting(true)
    try {
      const { error, projectId } = await acceptInvite(token)
      if (error) { setError(error.message); return }
      setDone(true)
      setTimeout(() => navigate(`/project/${projectId}`), 1400)
    } catch (e) {
      console.error('acceptInvite error:', e)
      setError('Something went wrong. Please try again.')
    } finally {
      setAccepting(false)
    }
  }, [token, acceptInvite, navigate])

  useEffect(() => {
    async function loadInvite() {
      if (!supabase) { setError('Supabase not configured.'); setLoading(false); return }
      try {
        const { data, error } = await supabase
          .from('project_invitations')
          .select('id, status, role, project_id, email')
          .eq('token', token)
          .single()
        if (error || !data) { setError('This invite link is invalid or has expired.'); return }
        if (data.status === 'accepted') { setError('This invite has already been used.'); return }
        if (data.email && currentUser?.email && data.email.toLowerCase() !== currentUser.email.toLowerCase()) {
          setError(`This invite was sent to ${data.email}. Please sign in with that account to accept.`)
          return
        }
        // Project details may fail if invitee isn't a member yet — that's fine
        const { data: project } = await supabase
          .from('projects')
          .select('name, color')
          .eq('id', data.project_id)
          .single()
        setInvite({ ...data, projects: project || { name: 'a project', color: '#e63946' } })
      } catch (e) {
        console.error('loadInvite error:', e)
        setError('Could not load this invite. Please check your connection and try again.')
      } finally {
        setLoading(false)
      }
    }
    loadInvite()
  }, [token])

  // Auto-accept once authenticated and invite is loaded
  useEffect(() => {
    if (isAuthenticated && invite && !done && !error) handleAccept()
  }, [isAuthenticated, invite, done, error, handleAccept])

  return (
    <div className="min-h-dvh flex items-center justify-center" style={{ background: '#0d0d0d' }}>
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, ease: [0.23, 1, 0.32, 1] }}
        className="w-full max-w-sm mx-4"
      >
        {/* Logo */}
        <div className="flex items-center gap-2.5 mb-8 justify-center">
          <div className="w-7 h-7 rounded-lg bg-accent/20 flex items-center justify-center text-accent text-xs font-bold">P</div>
          <span className="font-semibold text-sm text-text tracking-tight">PRJCT</span>
        </div>

        <div className="bg-surface border border-border rounded-2xl p-8 text-center">
          {loading ? (
            <p className="text-text-muted text-sm">Loading invite…</p>
          ) : error ? (
            <div>
              <p className="text-red-400 text-sm mb-4">{error}</p>
              <Link to="/" className="text-accent text-sm hover:underline">Go home →</Link>
            </div>
          ) : done ? (
            <div>
              <motion.div
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ type: 'spring', duration: 0.5, bounce: 0.3 }}
                className="w-12 h-12 rounded-full bg-green-400/10 flex items-center justify-center mx-auto mb-4"
              >
                <span className="text-green-400 text-xl">✓</span>
              </motion.div>
              <p className="text-text font-semibold">You're in!</p>
              <p className="text-text-muted text-sm mt-1">Taking you to the project…</p>
            </div>
          ) : (
            <div>
              {/* Project icon */}
              <div
                className="w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-5 text-2xl"
                style={{ background: (invite?.projects?.color || '#e63946') + '20', color: invite?.projects?.color || '#e63946', border: `1px solid ${invite?.projects?.color || '#e63946'}30` }}
              >
                ◈
              </div>
              <h1 className="text-lg font-bold text-text">You've been invited</h1>
              <p className="text-text-muted text-sm mt-1.5 mb-6">
                Join <span className="text-text font-semibold">{invite?.projects?.name}</span> as a {invite?.role}
              </p>

              {isAuthenticated ? (
                <Button variant="primary" className="w-full" onClick={handleAccept} disabled={accepting}>
                  {accepting ? 'Joining…' : 'Accept invite'}
                </Button>
              ) : (
                <div className="flex flex-col gap-3">
                  <Link to={`/signup?invite=${token}`}>
                    <Button variant="primary" className="w-full">Create account & join</Button>
                  </Link>
                  <Link to={`/auth?invite=${token}`}>
                    <Button variant="ghost" className="w-full">Sign in & join</Button>
                  </Link>
                </div>
              )}
            </div>
          )}
        </div>
      </motion.div>
    </div>
  )
}
